import type { BrowserLogger } from "@/logging/client/BrowserLogger";
import type {
	RouterInputs,
	RouterOutputs,
} from "@/setup/backend/trpc/trpcRouterTypes";
import type { KeyValueStorage } from "@/setup/frontend/storage/KeyValueStorage";

export const storeCareFormCreate = (
	storage: KeyValueStorage,
	offlineId: string,
	data: RouterInputs["careForms"]["create"],
) => {
	return storage.setItem(
		`care-form-create-${offlineId}`,
		JSON.stringify({
			...data,
			offline_id: offlineId,
		} satisfies RouterInputs["careForms"]["create"]),
	);
};

export const getCareFormCreate = async (
	storage: KeyValueStorage,
	offlineId: string,
) => {
	const item = await storage.getItem(`care-form-create-${offlineId}`);
	return item
		? (JSON.parse(item) as RouterInputs["careForms"]["create"])
		: null;
};

export const listCareFormCreates = async (storage: KeyValueStorage) => {
	return storage
		.keys()
		.then((keys) =>
			keys
				.filter((key) => key.startsWith("care-form-create-"))
				.map((key) => key.replace("care-form-create-", "")),
		);
};

export const removeCareFormCreate = (
	storage: KeyValueStorage,
	offlineId: string,
) => {
	return storage.removeItem(`care-form-create-${offlineId}`);
};

export const getCareFormCreateLastModified = async (
	storage: KeyValueStorage,
	offlineId: string,
) => {
	return storage.getLastModified(`care-form-create-${offlineId}`);
};

export const getCareFormEdit = async (
	storage: KeyValueStorage,
	id: number | undefined,
	offlineId: string | undefined,
) => {
	if (!id && !offlineId) {
		throw new Error(
			"Either id or offlineId must be provided to getCareFormEdit",
		);
	}

	const item = await storage.getItem(`care-form-edit-${id}`);
	return item
		? (JSON.parse(item) as RouterInputs["careForms"]["saveInProgress"])
		: null;
};

export const storeCareFormEdit = async (
	storage: KeyValueStorage,
	data: RouterInputs["careForms"]["saveInProgress"],
) => {
	const existingEdit = await getCareFormEdit(storage, data.id, undefined);
	const mergedData = existingEdit
		? {
				...existingEdit,
				...data,
				field_data: {
					...(existingEdit.field_data || {}),
					...(data.field_data || {}),
				},
			}
		: data;

	await storage.setItem(
		`care-form-edit-${data.id}`,
		JSON.stringify(mergedData),
	);
};

export const listCareFormEdits = async (storage: KeyValueStorage) => {
	const keys = await storage.keys();
	return keys
		.filter((key) => key.startsWith("care-form-edit-"))
		.map((key_1) => {
			const match = key_1.match(/care-form-edit-(\d+)$/);
			if (match) {
				return Number.parseInt(match[1], 10);
			}
			return null;
		})
		.filter((id): id is number => id !== null);
};

export const removeCareFormEdit = async (
	storage: KeyValueStorage,
	id: number,
	offlineId: string | undefined,
) => {
	return storage.removeItem(`care-form-edit-${id}`);
};

export const getCareFormEditLastModified = async (
	storage: KeyValueStorage,
	id: number | undefined,
	offlineId: string | undefined,
) => {
	return storage.getLastModified(`care-form-edit-${id}`);
};

export const storeCareFormDetail = async (
	storage: KeyValueStorage,
	data: RouterOutputs["careForms"]["detail"],
) => {
	await storage.setItem(`care-form-detail-${data.id}`, JSON.stringify(data));
};

export const getCareFormDetail = async (
	storage: KeyValueStorage,
	id: number | undefined,
	offlineId: string | undefined,
) => {
	if (!id && !offlineId) {
		throw new Error(
			"Either id or offlineId must be provided to getCareFormDetail",
		);
	}

	const item = await storage.getItem(`care-form-detail-${id}`);
	return item
		? (JSON.parse(item) as RouterOutputs["careForms"]["detail"])
		: null;
};

export const listCareFormDetailRecords = async (storage: KeyValueStorage) => {
	// get all stored records
	const keys = await storage
		.keys()
		.then((keys) => keys.filter((key) => key.startsWith("care-form-detail-")));
	return Promise.all(
		keys.map(async (key) => {
			const item = await storage.getItem(key);
			const id = key.replace("care-form-detail-", "");
			return item
				? ({
						...(JSON.parse(item) as RouterOutputs["careForms"]["detail"]),
						last_modified: await storage.getLastModified(key),
						last_modified_edit: await storage.getLastModified(
							`care-form-edit-${id}`,
						),
					} as RouterOutputs["careForms"]["detail"] & {
						last_modified: string;
						last_modified_edit: string;
					})
				: null;
		}),
	).then((records) =>
		records.filter(
			(record): record is NonNullable<typeof record> => record !== null,
		),
	);
};

export const removeCareFormDetail = async (
	storage: KeyValueStorage,
	id: number | undefined,
	offlineId: string | undefined,
) => {
	if (!id && !offlineId) {
		throw new Error(
			"Either id or offlineId must be provided to removeCareFormDetail",
		);
	}

	return storage.removeItem(`care-form-detail-${id}`);
};

export type CareFormTypePdfStorageEntry = Omit<
	RouterOutputs["careFormTypes"]["pdfDetail"],
	"download_url"
> & {
	blob: Blob;
};

export async function clearOldCareFormDetails(
	storage: KeyValueStorage,
	limit: number,
	logger: BrowserLogger,
) {
	try {
		const allKeys = await storage.keys();
		const detailKeys = allKeys.filter((key) =>
			key.startsWith("care-form-detail-"),
		);

		if (detailKeys.length <= limit) {
			return; // No cleanup needed
		}

		// Get cache entries with timestamps using getLastModified
		const cacheEntries = await Promise.all(
			detailKeys.map(async (key) => {
				try {
					const lastModified = await storage.getLastModified(key);
					if (!lastModified) return null;

					return {
						key,
						cachedAt: new Date(lastModified),
					};
				} catch (error) {
					logger.error(
						`Failed to get last modified for cache entry ${key}: ${error instanceof Error ? error.message : error}`,
					);
					return null;
				}
			}),
		);

		// Filter out null entries and sort by timestamp (oldest first)
		const validEntries = cacheEntries
			.filter((entry): entry is NonNullable<typeof entry> => entry !== null)
			.sort((a, b) => a.cachedAt.getTime() - b.cachedAt.getTime());

		// Remove the oldest entries to keep only limit
		const entriesToRemove = validEntries.slice(0, validEntries.length - limit);

		const removePromises = entriesToRemove.map(async (entry) => {
			try {
				await storage.removeItem(entry.key);
				logger.info(`Removed old cache entry: ${entry.key}`);
			} catch (error) {
				logger.error(
					`Failed to remove cache entry ${entry.key}: ${error instanceof Error ? error.message : error}`,
				);
			}
		});

		await Promise.allSettled(removePromises);

		logger.info(
			`Cleaned up ${entriesToRemove.length} old care form detail cache entries`,
		);
	} catch (error) {
		logger.error(
			`Failed to cleanup old detail caches. Error: ${error instanceof Error ? error.message : error}`,
		);
	}
}

const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result as string;
			// Remove the data URL prefix
			resolve(result.split(",")[1]);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
};

const base64ToBlob = (base64: string): Blob => {
	const binaryString = atob(base64);
	const bytes = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return new Blob([bytes], { type: "application/pdf" });
};

export const storeCareFormTypePdf = async (
	storage: KeyValueStorage,
	careFormTypeId: number,
	data: CareFormTypePdfStorageEntry,
) => {
	return storage.setItem(
		`care-form-type-pdf-${careFormTypeId}`,
		JSON.stringify({
			...data,
			blob: await blobToBase64(data.blob),
		}),
	);
};

export const getCareFormTypePdf = async (
	storage: KeyValueStorage,
	careFormTypeId: number,
) => {
	const item = await storage.getItem(`care-form-type-pdf-${careFormTypeId}`);
	if (item) {
		const parsed = JSON.parse(item);
		return {
			...parsed,
			blob: base64ToBlob(parsed.blob),
		} as CareFormTypePdfStorageEntry;
	}
	return null;
};

export const removeCareFormTypePdf = async (
	storage: KeyValueStorage,
	careFormTypeId: number,
) => {
	return storage.removeItem(`care-form-type-pdf-${careFormTypeId}`);
};

export const getCareFormTypePayerSourceOptions = async (
	storage: KeyValueStorage,
) => {
	const item = await storage.getItem("care-form-types-payer-source-options");
	if (item) {
		return JSON.parse(
			item,
		) as RouterOutputs["careFormTypes"]["payerSourceSelectOptions"];
	}
	return null;
};

export const storeCareFormTypePayerSourceOptions = async (
	storage: KeyValueStorage,
	data: RouterOutputs["careFormTypes"]["payerSourceSelectOptions"],
) => {
	await storage.setItem(
		"care-form-types-payer-source-options",
		JSON.stringify(data),
	);
};

export const removeCareFormTypePayerSourceOptions = async (
	storage: KeyValueStorage,
) => {
	await storage.removeItem("care-form-types-payer-source-options");
};

export const getCareFormTypeOptions = async (storage: KeyValueStorage) => {
	const item = await storage.getItem("care-form-types-options");
	if (item) {
		return JSON.parse(
			item,
		) as RouterOutputs["careFormTypes"]["creatableSelectOptions"];
	}
	return null;
};

export const storeCareFormTypeOptions = async (
	storage: KeyValueStorage,
	data: RouterOutputs["careFormTypes"]["creatableSelectOptions"],
) => {
	await storage.setItem("care-form-types-options", JSON.stringify(data));
};

export const removeCareFormTypeOptions = async (storage: KeyValueStorage) => {
	await storage.removeItem("care-form-types-options");
};
